import { PropertyTypeId } from "./propertyType";
import * as React from "react";
import { CalculationResponseModel } from "./calculation";
import { RisksNetworkResponseModel, RisksNetworkTypes } from "./risksNetwork";

export interface RiskModel {
  available?: boolean;
  editable?: boolean;
  hideInsuranceSumFromResponse?: boolean;
  insuranceSum?: number;
  invalidInsuranceSum?: boolean;
  suggestedInsuranceSum?: number;
  getInsuranceSumFromResponse?: (response: CalculationResponseModel) => number | undefined;
  contextHelp?: ContextHelpModel[];
  hasError?: boolean;
  risksNetworkValue?: RisksNetworkTypes;
  getRisksNetworkValue?: (response: RisksNetworkResponseModel) => RisksNetworkTypes;
  getInsuranceSumFromRetrievedCalculation?: (retrievedCalculation: any) => number | null | undefined;
  getAvailableFromRetrievedCalculation?: (retrievedCalculation: any) => boolean;
}

export interface ContextHelpModel {
  id: string;
  available: boolean;
  additionalInfo?: string | React.ReactNode;
}

export enum RiskModeId {
  allRisk = "ALL_RISK",
  namedRisk = "NAMED_RISK"
}

export enum RisksId {
  detachedHouseOrFlatWithFixedElements = "detachedHouseOrFlatWithFixedElements",
  onlyFixedElements = "onlyFixedElements",
  movables = "movables",
  burglaryAndTheftMovables = "burglaryAndTheftMovables",
  fixedElementsWithBurglaryAndTheft = "fixedElementsWithBurglaryAndTheft",
  theft = "theft",
  assistance = "assistance",
  risksInOwu = "risksInOwu",
  riskMode = "riskMode",
  devastation = "devastation",
  surges = "surges",
  windowpanes = "windowpanes",
  flood = "flood",
  buildingToolsAndMaterials = "buildingToolsAndMaterials",
  searchingAndRemovingCauseOfDamage = "searchingAndRemovingCauseOfDamage",
  nonResidentialBuilding = "nonResidentialBuilding",
  fixedEstateElements = "fixedEstateElements",
  greenEnergySources = "greenEnergySources",
  catAndDog = "catAndDog",
  gravestone = "gravestone",
  ocWithoutProperty = "ocWithoutProperty",
  ocStandard = "ocStandard",
  ocChildExtension = "ocChildExtension",
  ocRenterExtension = "ocRenterExtension",
  ocAdditionalProperty = "ocAdditionalProperty",
  legalProtection = "legalProtection",
  nnwWithoutProperty = "nnwWithoutProperty",
  nnwType = "nnwType",
  nnwSenior = "nnwSenior",
  nnwHeartAttackAndStroke = "nnwHeartAttackAndStroke",
  safeSenior = "safeSenior",
  secondOpinion = "secondOpinion",
  secondOpinionExtension = "secondOpinionExtension"
}

export class Risk {
  private static readonly defaultRiskList = [
    RisksId.detachedHouseOrFlatWithFixedElements,
    RisksId.onlyFixedElements,
    RisksId.movables,
    RisksId.burglaryAndTheftMovables,
    RisksId.fixedElementsWithBurglaryAndTheft,
    RisksId.theft,
    RisksId.assistance,
    RisksId.risksInOwu,
    RisksId.riskMode,
    RisksId.devastation,
    RisksId.surges,
    RisksId.windowpanes,
    RisksId.flood
  ];

  private static readonly riskListForUnderConstructionOpenState = [
    RisksId.detachedHouseOrFlatWithFixedElements,
    RisksId.risksInOwu,
    RisksId.flood
  ];

  private static readonly riskListForUnderConstructionCloseState = [
    RisksId.detachedHouseOrFlatWithFixedElements,
    RisksId.buildingToolsAndMaterials,
    RisksId.fixedElementsWithBurglaryAndTheft,
    RisksId.risksInOwu,
    RisksId.devastation,
    RisksId.flood
  ];

  public riskModeDisplayName = {
    allRisk: "All risk",
    namedRisk: "Ryzyka nazwane"
  };

  private static _risksInPackageDisplayName = {
    detachedHouseOrFlatWithFixedElements: (propertyType: PropertyTypeId) => {
      switch (propertyType) {
        case PropertyTypeId.flatUnderConstructionOpenState:
        case PropertyTypeId.detachedHouseUnderConstructionOpenState:
          return "Dom/Mieszkanie bez stałych elementów";
        default:
          return "Dom/Mieszkanie ze stałymi elementami";
      }
    },
    onlyFixedElements: "Tylko stałe elementy bez domu/mieszkania",
    movables: "Ruchomości domowe",
    burglaryAndTheftMovables: "Ruchomości domowe od kradzieży z włamaniem i rabunku",
    fixedElementsWithBurglaryAndTheft: "Stałe elementy od kradzieży z włamaniem i rabunku",
    theft: "Kradzież zwykła",
    assistance: "Assistance Home-Med",
    risksInOwu: "Ryzyka wymienione w OWU",
    riskMode: "Pozostałe zdarzenia - All risk",
    devastation: "Dewastacja",
    surges: "Przepięcia",
    windowpanes: "Stłuczenie szyby i elementy szklane",
    flood: "Powódź",
    buildingToolsAndMaterials: "Materiały budowlane i narzędzia budowlane",
    searchingAndRemovingCauseOfDamage: "Poszukiwanie i u sunięcie przyczyny szkody"
  };

  public static risksInPackageDisplayName(riskId: RisksId, propertyType: PropertyTypeId | null): string {
    // @ts-ignore
    const displayName = Risk._risksInPackageDisplayName[riskId];
    if (typeof displayName === "string") {
      return displayName;
    }
    if (typeof displayName === "function") {
      return displayName(propertyType);
    }
    return displayName as string;
  }

  public static contextHelpDescriptionDefault = {
    [RisksId.detachedHouseOrFlatWithFixedElements]: {
      detachedHouseOrFlatWithFixedElements1: "Ochrona całej nieruchomości m.in. murów, instalacji, okien, okładzin ścian i podłóg, zabudowanych mebli w zakresie m.in. takich zdarzeń jak pożar, zalanie"
    },
    [RisksId.onlyFixedElements]: {
      onlyFixedElements1: "Ochrona stałych elementów np. zabudowy mebli, okładziny ścian i podłóg, okna, drzwi, instalacje w zakresie m.in. takich zdarzeń jak pożar, zalanie"
    },
    [RisksId.movables]: {
      movables1: "Ochrona ruchomości domowych w zakresie zdarzeń m.in. takich jak np. pożar, zalanie",
      movables2: "Rzeczy gości",
      movables3: "Rzeczy w czasowym posiadaniu (w tym mienie służbowe)",
      movables4: "Ruchomości domowe w pomieszczeniach przynależnych i budynkach niemieszkalnych",
      movables5: "Ruchomości domowe na zabudowanych: tarasach, balkonach, loggiach, ogrodach zimowych",
      movables6: "Przedmioty wartościowe (m.in. biżuteria, zegarki, dzieła sztuki)",
      movables7: "Wartości pieniężne (gotówka)",
      movables8: "Futra, odzież ze skór naturalnych i sprzęt medyczny",
      movables9: "Roślinność doniczkowa",
      movables10: "Roślinność ogrodowa",
      movables11: "Ruchomości domowe wykorzystywane do działalności gospodarczej (dla nieruchomości zadeklarowanych, że są wykorzystywane do działalności gospodarczych)",
      movables12: "Mienie podręczne poza domem (pod warunkiem ubezpieczenia ruchomości domowych na sumę ubezpieczenia wynoszącą nie mniej niż 15 000 zł od ryzyk nazwanych albo od wszystkich ryzyk (All Risk) oraz od kradzieży z włamaniem i rabunku)"
    },
    [RisksId.burglaryAndTheftMovables]: {
      burglaryAndTheftMovables1: "Kradzież ruchomości domowych z zamkniętych pomieszczeń",
      burglaryAndTheftMovables2: "Zabór ruchomości domowych przy użyciu lub groźbie natychmiastowego użycia przemocy fizycznej",
      burglaryAndTheftMovables3: "Rzeczy gości",
      burglaryAndTheftMovables4: "Rzeczy w czasowym posiadaniu (w tym mienie służbowe)",
      burglaryAndTheftMovables5: "Ruchomości domowe w pomieszczeniach przynależnych i budynkach niemieszkalnych",
      burglaryAndTheftMovables6: "Ruchomości domowe na zabudowanych: tarasach, balkonach, logiach, ogrodach zimowych",
      burglaryAndTheftMovables7: "Przedmioty wartościowe (m.in. biżuteria, zegarki)",
      burglaryAndTheftMovables8: "Wartości pieniężne (gotówka)",
      burglaryAndTheftMovables9: "Futra, odzież ze skór naturalnych i sprzęt medyczny",
      burglaryAndTheftMovables10: "Roślinność doniczkowa",
      burglaryAndTheftMovables11: "Ruchomości domowe wykorzystywane do działalności gospodarczej (dla nieruchomości zadeklarowanych, że są wykorzystywane do działalności gospodarczych)",
      burglaryAndTheftMovables12: "Mienie podręczne poza domem (pod warunkiem ubezpieczenia ruchomości domowych na sumę ubezpieczenia wynoszącą nie mniej niż 15 000 zł od ryzyk nazwanych albo od wszystkich ryzyk (All Risk) oraz od kradzieży z włamaniem i rabunku)"
    },
    [RisksId.fixedElementsWithBurglaryAndTheft]: {
      fixedElementsWithBurglaryAndTheft1: "Kradzież stałych elementów z zamkniętych pomieszczeń",
      fixedElementsWithBurglaryAndTheft2: "Zabór stałych elementów przy użyciu lub groźbie natychmiastowego użycia przemocy fizycznej"
    },
    [RisksId.theft]: {
      theft1: "Zewnętrznych elementów budynków, obiektów małej architektury, zielonych źródeł energii - o ile zostały ubezpieczone  w zakresie od ryzyk nazwanych albo od wszystkich ryzyk (All Risk)",
      theft2: "Roślinności ogrodowej, mebli ogrodowych, o ile rozszerzono ochronę o ruchomości domowe w zakresie kradzieży z włamaniem"
    },
    [RisksId.assistance]: {
      assistance1: "Assistance Techniczny (m.in.: ślusarz, elektryk, hydraulik, specjalista od urządzeń grzewczych)",
      assistance2: "Assistance AGD/RTV (m.in.: naprawa lodówki, pralki)",
      assistance3: "Assistance Medyczny (m.in.: wizyta lekarza lub pielęgniarki, eKonsultacja, dostawa leków)",
      assistance4: "Pomoc Informatyka (zdalna pomoc informatyka)",
      assistance5: "Pakiet Pomocy (gdy nieruchomość po szkodzie nie nadaje się do  mieszkania)",
      assistance6: "Pakiet Informacyjny (np. Udzielanie informacji o serwisach)"

    },
    [RisksId.risksInOwu]: {
      risksInOwu1: "Szkody w wyniku rażącego niedbalstwa",
      risksInOwu2: "deszcz nawalny, pożar, dym i sadza, wybuch, grad, huk ponaddźwiękowy, lawina,  osunięcie się ziemi, uderzenie pioruna, silny wiatr, napór śniegu, trzęsienie ziemi, uderzenie pojazdu, upadek przedmiotu, upadek statku powietrznego, zapadanie się ziemi",
      risksInOwu3: "Zalanie",
      risksInOwu4: "w tym koszty poszukiwania przyczyny szkody",
      risksInOwu5: "Koszty utraty wody lub gazu",
      risksInOwu6: "Szkody wyrządzone przez zwierzęta (z wyłączeniem zwierząt domowych, owadów, kretów)",
      risksInOwu7: "Szkody wyrządzone przez zwierzęta (z wyłączeniem zwierząt domowych, owadów, kretów) w samochodzie osobowym, motocyklu, motorowerze.",
      risksInOwu8: "Pozostałe zdarzenia - All Risk"
    },
    [RisksId.riskMode]: {
      riskMode1: "Wszystkie nagłe i nieprzewidziane zdarzenia, które nie zostały wyłączone w OWU"
    },
    [RisksId.devastation]: {
      devastation1: "Umyślne zniszczenie mienia przez osoby trzecie",
      devastation2: "Dewastacja w związku z kradzieżą i rabunkiem",
      devastation3: "Graffiti"
    },
    [RisksId.windowpanes]: {
      windowpanes1: "Przypadkowe rozbicie, pęknięcie lub stłuczenie m.in.: szyb okiennych i drzwiowych, przegród ściennych, osłon balkonów, tarasów, ogrodów zimowych, okładzin ścian, osłon kabin prysznicowych, płyt grzewczych, luster przytwierdzonych na stałe, szyb kominkowych, piekarnika, akwarium"
    },
    [RisksId.surges]: {
      surges1: "Gwałtowna zmiana napięcia w sieci elektrycznej lub elektronicznej, w tym będący skutkiem pośredniego wyładowania atmosferycznego",
      surges2: "w tym koszty poszukiwania przyczyny szkody"
    },
    [RisksId.flood]: {
      flood1: "Zalanie terenu w następstwie podniesienia się poziomu wody w korytach wód płynących, naturalnych lub sztucznych zbiornikach wód, na skutek: nadmiernych opadów atmosferycznych, spływu wód po zboczach lub stokach na terenach górzystych lub  pofałdowanych, topnienia kry lodowej, tworzenia się zatorów lodowych w korytach wód płynących, sztormu i podniesienia się poziomu morskich wód przybrzeżnych"
    }
  };

  public static contextHelpDescriptionForUnderConstructionOpenState = {
    [RisksId.detachedHouseOrFlatWithFixedElements]: {
      detachedHouseOrFlatWithFixedElements1: "Ochrona murów, fundamentów, dachu, elewacji, tynków zewnętrznych bez stałych elementów"
    },
    [RisksId.risksInOwu]: {
      risksInOwu1: "Szkody w wyniku rażącego niedbalstwa",
      risksInOwu2: "deszcz nawalny, pożar, dym i sadza, wybuch, grad, huk ponaddźwiękowy, lawina,  osunięcie się ziemi, uderzenie pioruna, silny wiatr, napór śniegu, trzęsienie ziemi, uderzenie pojazdu, upadek przedmiotu, upadek statku powietrznego, zapadanie się ziemi"
    },
    [RisksId.flood]: {
      flood1: "Zalanie terenu w następstwie podniesienia się poziomu wody w korytach wód płynących, naturalnych lub sztucznych zbiornikach wód, na skutek: nadmiernych opadów atmosferycznych, spływu wód po zboczach lub stokach na terenach górzystych lub  pofałdowanych, topnienia kry lodowej, tworzenia się zatorów lodowych w korytach wód płynących, sztormu i podniesienia się poziomu morskich wód przybrzeżnych"
    }
  };

  public static contextHelpDescriptionForUnderConstructionClosedState = {
    [RisksId.detachedHouseOrFlatWithFixedElements]: {
      detachedHouseOrFlatWithFixedElements1: "Ochrona całej nieruchomości m.in.. murów, instalacji, okien, okładzin ścian i podłóg, zabudowanych mebli w zakresie zdarzeń nagłych i nieprzewidzianych np. pożar, zalanie"
    },
    [RisksId.risksInOwu]: {
      risksInOwu1: "Szkody w wyniku rażącego niedbalstwa",
      risksInOwu2: "deszcz nawalny, pożar, dym i sadza, wybuch, grad, huk ponaddźwiękowy, lawina,  osunięcie się ziemi, uderzenie pioruna, silny wiatr, napór śniegu, trzęsienie ziemi, uderzenie pojazdu, upadek przedmiotu, upadek statku powietrznego, zapadanie się ziemi",
      risksInOwu3: "Zalanie",
      risksInOwu4: "w tym koszty poszukiwania i usunięcia szkody"
    },
    [RisksId.fixedElementsWithBurglaryAndTheft]: {
      fixedElementsWithBurglaryAndTheft1: "Kradzież stałych elementów z zamkniętych pomieszczeń",
      fixedElementsWithBurglaryAndTheft2: "Zabór stałych elementów przy użyciu lub groźbie natychmiastowego użycia przemocy fizycznej"
    },
    [RisksId.buildingToolsAndMaterials]: {
      buildingToolsAndMaterials1: "Narzędzia, materiały budowlane, stałe elementy przed ich zamontowaniem przeznaczone do prac wykończeniowych i adaptacyjnych oraz ruchomości domowe."
    },
    [RisksId.devastation]: {
      devastation1: "Umyślne zniszczenie mienia przez osoby trzecie",
      devastation2: "Dewastacja w związku z kradzieżą i rabunkiem",
      devastation3: "Graffiti"
    },
    [RisksId.flood]: {
      flood1: "Zalanie terenu w następstwie podniesienia się poziomu wody w korytach wód płynących, naturalnych lub sztucznych zbiornikach wód, na skutek: nadmiernych opadów atmosferycznych, spływu wód po zboczach lub stokach na terenach górzystych lub  pofałdowanych, topnienia kry lodowej, tworzenia się zatorów lodowych w korytach wód płynących, sztormu i podniesienia się poziomu morskich wód przybrzeżnych"
    }
  };

  public static getContextHelpDescription(propertyType: PropertyTypeId) {
    switch (propertyType) {
      case PropertyTypeId.detachedHouseUnderConstructionOpenState:
      case PropertyTypeId.flatUnderConstructionOpenState:
        return Risk.contextHelpDescriptionForUnderConstructionOpenState;
      case PropertyTypeId.detachedHouseUnderConstructionClosedState:
      case PropertyTypeId.flatUnderConstructionClosedState:
        return Risk.contextHelpDescriptionForUnderConstructionClosedState;
      default:
        return Risk.contextHelpDescriptionDefault;
    }
  }

  public static getRiskInPackageValue(riskId: RisksId, risks: Map<RisksId, RiskModel>) {
    if (!risks.has(riskId)) {
      return false;
    }

    const risk = risks.get(riskId);

    switch (risk!.risksNetworkValue) {
      case RisksNetworkTypes.available:
        return risk!.available;
      case RisksNetworkTypes.notAvailable:
        return false;
      case RisksNetworkTypes.required:
        return true;
      default:
        return risk!.available;
    }
  }

  public static getRiskInPackageInsuranceSum(riskId: RisksId, risks: Map<RisksId, RiskModel>): number {
    if (!risks.has(riskId)) {
      return 0;
    }

    return risks.get(riskId)!.insuranceSum ? risks.get(riskId)!.insuranceSum! : 0;
  }

  public static getRiskMode(risks: Map<RisksId, RiskModel>) {
    if (!risks.has(RisksId.riskMode)) {
      return RiskModeId.namedRisk;
    }

    return risks.get(RisksId.riskMode)!.available ? RiskModeId.allRisk : RiskModeId.namedRisk;
  }

  public static isPropertyTypesHaveRiskDifference(fromPropertyType: PropertyTypeId | null | undefined, toPropertyType: PropertyTypeId | null | undefined): boolean {
    if (!fromPropertyType || !toPropertyType) {
      return true;
    }
    const fromPropertyRiskIds = this.getRisksListInPackage(fromPropertyType).slice().sort();
    const toPropertyRiskIds = this.getRisksListInPackage(toPropertyType).slice().sort();

    return JSON.stringify(fromPropertyRiskIds) !== JSON.stringify(toPropertyRiskIds);
  }

  public static getRisksListInPackage(propertyType: PropertyTypeId): RisksId[] {

    switch (propertyType) {
      case PropertyTypeId.detachedHouseUnderConstructionOpenState:
      case PropertyTypeId.flatUnderConstructionOpenState:
        return Risk.riskListForUnderConstructionOpenState;
      case PropertyTypeId.detachedHouseUnderConstructionClosedState:
      case PropertyTypeId.flatUnderConstructionClosedState:
        return Risk.riskListForUnderConstructionCloseState;
      default:
        return Risk.defaultRiskList;
    }
  }
}
